type Result = {
  isValid: boolean,
  errors: Record<string, string>,
}

type ValidationFunc<E extends object> =
  ((itemValue: unknown, entityState: E) => boolean) | undefined

// Since keys can be nested strings (e.g., 'data.nested_valid'),
// we represent the key as a general string path.
type Field<E extends object> = {
  key: string, // A string path to allow for "data.nested_key"
  invalidMessage?: string,
  validation?: ValidationFunc<E>,
}

const performValidation = <E extends object>(
  validation: ValidationFunc<E>,
  itemValue: unknown,
  entityState: E,
): boolean => {
  if (validation && typeof validation === 'function'){
    return validation(itemValue, entityState)
  }

  // Default validation: returns true if value is truthy (not null, undefined, "", 0)
  return !!itemValue
}

function isValidRequiredEntity<E extends object>(
  requiredFields: Field<E>[],
  entityState: E,
  dirtyFields?: string[],
): Result {
  const defaultResult: Result = {
    isValid: true,
    errors: {},
  }

  const result = requiredFields.reduce((accumulator: Result, item) => {
    const { isValid, errors } = accumulator
    const { key, validation, invalidMessage } = item

    const itemValue: unknown = key.split('.').reduce((acc, k) => acc?.[k], entityState as any)

    // Check if the item has been dirtied
    const isItemDirty = dirtyFields ? dirtyFields.includes(key) : false

    // Perform validation
    const isItemValid = performValidation(validation, itemValue, entityState)

    const shouldShowError = isItemDirty && !isItemValid

    let errorMessage = ''
    if (!isItemValid){
      // If the value is falsy (empty, null, undefined) it's a required error.
      if (!itemValue){
        errorMessage = 'Required'
      // If the value is present but failed custom validation, use the custom message.
      } else {
        errorMessage = invalidMessage ?? 'Invalid'
      }
    }

    const updatedErrors = shouldShowError
      ? { ...errors, [key]: errorMessage }
      : errors

    return {
      errors: updatedErrors,
      isValid: isValid && isItemValid,
    }
  }, defaultResult)

  return result
}

export default isValidRequiredEntity
